import React from "react";
import { Navigate } from "react-router-dom";
import { PLAN_MODULES } from "../../hooks/useAccessControl";
import NoAccessScreen from "./NoAccessScreen";

// ─────────────────────────────────────────────────────────────────────────────
// Resolve a module key (alias or canonical) to its canonical backend value.
// e.g. "USERS" → "USER_MANAGEMENT", "TABLES" → "TABLE_AND_LOCATION"
// ─────────────────────────────────────────────────────────────────────────────
const resolveModule = (key: string): string =>
  (PLAN_MODULES as Record<string, string>)[key] ?? key;

// Human-readable display name from a module key (e.g. "BASIC_MENU" → "Basic Menu")
const moduleDisplayName = (key: string): string => {
  const resolved = resolveModule(key);
  return resolved
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

interface AccessProtectedRouteProps {
  children: React.ReactNode;
  /** Require specific module access (e.g., "MENU", "INVENTORY") */
  requireModule?: string;
  /** Require any of these modules */
  requireAnyModule?: string[];
  /** Require all of these modules */
  requireAllModules?: string[];
  /** Require specific permission (e.g., "MENU_FULL_ACCESS", "EDIT_INGREDIENT_ACCESS") */
  requirePermission?: string;
  /** Require any of these permissions */
  requireAnyPermission?: string[];
  /** Require all of these permissions */
  requireAllPermissions?: string[];
  /** Override the feature name shown in the NoAccessScreen */
  featureName?: string;
}

/**
 * Route-level access guard.
 *
 * Checks user permissions from localStorage (set during login).
 * On failure: renders a NoAccessScreen inline (stays within the app layout).
 * Backend validates all API calls — this is UI/UX only.
 */
export const AccessProtectedRoute: React.FC<AccessProtectedRouteProps> = ({
  children,
  requireModule,
  requireAnyModule,
  requireAllModules,
  requirePermission,
  requireAnyPermission,
  requireAllPermissions,
  featureName,
}) => {
  const token = localStorage.getItem("token");
  const modulesStr = localStorage.getItem("includedModules");
  const permissionsStr = localStorage.getItem("grantedPermissions");

  // Not authenticated → go to login
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const modules: string[] = (() => {
    try { return modulesStr ? JSON.parse(modulesStr) : []; } catch { return []; }
  })();
  const permissions: string[] = (() => {
    try { return permissionsStr ? JSON.parse(permissionsStr) : []; } catch { return []; }
  })();

  // If no checks specified, allow access
  const hasAnyCheck =
    requireModule ||
    requireAnyModule ||
    requireAllModules ||
    requirePermission ||
    requireAnyPermission ||
    requireAllPermissions;

  if (!hasAnyCheck) {
    return <>{children}</>;
  }

  const isOwnerOrAdmin = localStorage.getItem("isOwner") === "true" || localStorage.getItem("isAdmin") === "true";
  const moduleFailReason = isOwnerOrAdmin ? "plan_blocked" : "permission_denied";

  // ── Module checks (plan_blocked reason) ─────────────────────────────────

  if (requireModule) {
    const canonical = resolveModule(requireModule);
    if (modules.length > 0 && !modules.includes(canonical)) {
      const name = featureName ?? moduleDisplayName(requireModule);
      console.warn(`⛔ Module "${requireModule}" (→ "${canonical}") not in plan`, { modules });
      return <NoAccessScreen reason={moduleFailReason} featureName={name} />;
    }
  }

  if (requireAnyModule && requireAnyModule.length > 0) {
    const hasAny = requireAnyModule.some((m) => modules.includes(resolveModule(m)));
    if (modules.length > 0 && !hasAny) {
      const name = featureName ?? requireAnyModule.map(moduleDisplayName).join(" / ");
      console.warn(`⛔ None of modules found: ${requireAnyModule.join(", ")}`, { modules });
      return <NoAccessScreen reason={moduleFailReason} featureName={name} />;
    }
  }

  if (requireAllModules && requireAllModules.length > 0) {
    const hasAll = requireAllModules.every((m) => modules.includes(resolveModule(m)));
    if (modules.length > 0 && !hasAll) {
      const name = featureName ?? requireAllModules.map(moduleDisplayName).join(" + ");
      console.warn(`⛔ Not all modules found: ${requireAllModules.join(", ")}`, { modules });
      return <NoAccessScreen reason={moduleFailReason} featureName={name} />;
    }
  }

  // ── Permission checks (permission_denied reason) ─────────────────────────

  if (requirePermission) {
    if (permissions.length > 0 && !permissions.includes(requirePermission)) {
      const name = featureName ?? moduleDisplayName(requirePermission);
      console.warn(`⛔ Permission "${requirePermission}" not granted`, { totalPermissions: permissions.length });
      return <NoAccessScreen reason="permission_denied" featureName={name} />;
    }
  }

  if (requireAnyPermission && requireAnyPermission.length > 0) {
    const hasAny = requireAnyPermission.some((p) => permissions.includes(p));
    if (permissions.length > 0 && !hasAny) {
      const name = featureName ?? requireAnyPermission.map(moduleDisplayName).join(" / ");
      console.warn(`⛔ None of permissions granted: ${requireAnyPermission.join(", ")}`);
      return <NoAccessScreen reason="permission_denied" featureName={name} />;
    }
  }

  if (requireAllPermissions && requireAllPermissions.length > 0) {
    const hasAll = requireAllPermissions.every((p) => permissions.includes(p));
    if (permissions.length > 0 && !hasAll) {
      const name = featureName ?? requireAllPermissions.map(moduleDisplayName).join(" + ");
      console.warn(`⛔ Not all permissions granted: ${requireAllPermissions.join(", ")}`);
      return <NoAccessScreen reason="permission_denied" featureName={name} />;
    }
  }

  // All checks passed
  return <>{children}</>;
};
